/**
 * 管理员后台：会员管理
 *
 * 使用独立的 admin_token Cookie 认证，
 * 与角色管理端（/parent）分离。
 */

import { useEffect, useState } from 'react'
import { adminLogin, adminLogout, getAdminMe, listMembers, MemberWithUsage, getMemberDetail } from '../api'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

export default function AdminDashboard() {
  const [admin, setAdmin] = useState<{ id: string; username: string } | null>(null)
  const [members, setMembers] = useState<MemberWithUsage[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedMember, setSelectedMember] = useState<MemberWithUsage | null>(null)
  const [detail, setDetail] = useState<any>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const checkAuth = async () => {
    try {
      const res = await getAdminMe()
      setAdmin(res.admin)
      await loadMembers()
    } catch {
      setAdmin(null)
    } finally {
      setLoading(false)
    }
  }

  const loadMembers = async () => {
    try {
      const res = await listMembers()
      setMembers(res.members)
    } catch (err) {
      console.error('Failed to load members', err)
    }
  }

  useEffect(() => {
    checkAuth()
  }, [])

  const handleLogin = async (username: string, password: string) => {
    await adminLogin(username, password)
    await checkAuth()
  }

  const handleLogout = async () => {
    try {
      await adminLogout()
    } catch {}
    setAdmin(null)
    setMembers([])
    setSelectedMember(null)
    setDetail(null)
  }

  const openDetail = async (member: MemberWithUsage) => {
    setSelectedMember(member)
    setDetailLoading(true)
    try {
      const res = await getMemberDetail(member.id)
      setDetail(res)
    } catch (err) {
      console.error('Failed to load member detail', err)
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">加载中...</div>
  }

  if (!admin) {
    return <AdminLoginForm onLogin={handleLogin} />
  }

  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-800">
      {/* 顶部 */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center text-xl">
            👑
          </div>
          <div>
            <h1 className="font-bold text-lg">管理员后台</h1>
            <p className="text-xs text-slate-500">会员与流量管理</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600 hidden sm:inline">管理员：{admin.username}</span>
          <a
            href="/"
            className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors"
          >
            首页
          </a>
          <button
            onClick={handleLogout}
            className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition-colors"
          >
            退出
          </button>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {/* 概览卡片 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <SummaryCard label="总会员" value={members.length.toString()} icon="👥" />
          <SummaryCard
            label="总聊天回合"
            value={members.reduce((sum, m) => sum + m.chat_turns, 0).toString()}
            icon="💬"
          />
          <SummaryCard
            label="总流量下行"
            value={formatBytes(members.reduce((sum, m) => sum + m.total_output_bytes, 0))}
            icon="⬇️"
          />
        </div>

        {/* 会员列表 */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-800">会员列表</h2>
            <button
              onClick={loadMembers}
              className="text-sm text-violet-600 font-semibold hover:text-violet-700"
            >
              刷新
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-6 py-3 font-medium">邮箱</th>
                  <th className="px-6 py-3 font-medium">用户名</th>
                  <th className="px-6 py-3 font-medium">注册时间</th>
                  <th className="px-6 py-3 font-medium">聊天回合</th>
                  <th className="px-6 py-3 font-medium">总时长</th>
                  <th className="px-6 py-3 font-medium">总下行</th>
                  <th className="px-6 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50">
                    <td className="px-6 py-3">{m.email}</td>
                    <td className="px-6 py-3">{m.username || '-'}</td>
                    <td className="px-6 py-3 text-slate-500">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString('zh-CN') : '-'}
                    </td>
                    <td className="px-6 py-3">{m.chat_turns}</td>
                    <td className="px-6 py-3">{Math.round(m.total_duration_seconds / 60)}m</td>
                    <td className="px-6 py-3">{formatBytes(m.total_output_bytes)}</td>
                    <td className="px-6 py-3">
                      <button
                        onClick={() => openDetail(m)}
                        className="text-violet-600 font-semibold hover:underline"
                      >
                        详情
                      </button>
                    </td>
                  </tr>
                ))}
                {members.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-slate-400">
                      暂无会员
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* 详情模态 */}
      {selectedMember && (
        <MemberDetailModal
          member={selectedMember}
          detail={detail}
          loading={detailLoading}
          onClose={() => {
            setSelectedMember(null)
            setDetail(null)
          }}
        />
      )}
    </div>
  )
}

function SummaryCard({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 text-white text-xl flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-800">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  )
}

function AdminLoginForm({ onLogin }: { onLogin: (username: string, password: string) => Promise<void> }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await onLogin(username, password)
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white/80 backdrop-blur-xl rounded-3xl shadow-[0_12px_40px_-12px_rgba(100,116,139,0.2)] border border-white/60 p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white text-3xl mb-4">
            👑
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Talk-o 管理员登录</h1>
          <p className="text-slate-500 text-sm mt-1">会员与流量管理后台</p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">管理员账号</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-sm"
              placeholder="admin"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">管理员密码</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-sm"
              placeholder="请输入管理员密码"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-700 hover:to-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl text-sm shadow-lg shadow-sky-200 transition-all"
          >
            {loading ? '登录中...' : '登录后台'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a href="/" className="text-xs text-slate-400 hover:text-slate-600">
            ← 返回首页
          </a>
        </div>
      </div>
    </div>
  )
}

function MemberDetailModal({
  member,
  detail,
  loading,
  onClose,
}: {
  member: MemberWithUsage
  detail: any
  loading: boolean
  onClose: () => void
}) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-bold text-lg text-slate-800">
            {member.username || member.email} 的详情
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">
            &times;
          </button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-slate-500 text-sm">加载中...</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <Info label="邮箱" value={member.email} />
                <Info label="用户名" value={member.username || '-'} />
                <Info label="聊天回合" value={String(member.chat_turns)} />
                <Info label="总时长" value={`${Math.round(member.total_duration_seconds / 60)}m`} />
              </div>
              <h4 className="font-bold text-slate-800 mb-3">最近使用记录</h4>
              {detail?.usage_events?.length > 0 ? (
                <ul className="space-y-2 max-h-60 overflow-y-auto pr-2">
                  {detail.usage_events.map((event: any) => (
                    <li key={event.id} className="text-sm bg-slate-50 rounded-lg px-4 py-2 flex items-center justify-between">
                      <span className="font-medium text-slate-700">{event.feature}</span>
                      <span className="text-slate-500">
                        {event.created_at ? new Date(event.created_at).toLocaleString('zh-CN') : '-'}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400">暂无详细使用记录</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 rounded-xl px-4 py-3">
      <div className="text-xs text-slate-400 mb-0.5">{label}</div>
      <div className="font-semibold text-slate-800 truncate">{value}</div>
    </div>
  )
}
